import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { Chip, Tooltip } from "@mui/material";
import { type MouseEvent, useEffect, useRef, useState } from "react";

type DocumentsTableDocumentIdentifierProps = {
  documentId: string;
  documentIdentifier?: string | null;
};

export const DocumentsTableDocumentIdentifier = ({
  documentId,
  documentIdentifier,
}: DocumentsTableDocumentIdentifierProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const resetCopyStateTimeoutRef = useRef<number | undefined>();
  const documentIdentifierValue = documentIdentifier || documentId;

  useEffect(
    () => () => {
      if (resetCopyStateTimeoutRef.current) {
        window.clearTimeout(resetCopyStateTimeoutRef.current);
      }
    },
    []
  );

  if (!documentId) {
    return null;
  }

  const handleCopyDocumentId = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const copyButton = event.currentTarget;
    const shouldBlurAfterCopy = event.detail > 0;

    void navigator.clipboard.writeText(documentIdentifierValue).then(() => {
      setIsCopied(true);
      if (shouldBlurAfterCopy) {
        copyButton.blur();
      }

      if (resetCopyStateTimeoutRef.current) {
        window.clearTimeout(resetCopyStateTimeoutRef.current);
      }

      resetCopyStateTimeoutRef.current = window.setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    });
  };

  return (
    <Tooltip title={documentIdentifierValue} placement="top" describeChild>
      <Chip
        aria-label={isCopied ? "Document ID copied" : "Copy document ID"}
        component="button"
        data-testid="documents-table-document-identifier"
        icon={
          isCopied ? (
            <CheckRoundedIcon data-testid="documents-table-document-id-copied-icon" />
          ) : (
            <ContentCopyRoundedIcon data-testid="documents-table-document-id-copy-icon" />
          )
        }
        label="ID"
        onClick={handleCopyDocumentId}
        onMouseDown={(event) => event.stopPropagation()}
        size="xxs"
        sx={{
          borderColor: isCopied ? "#22c55e" : undefined,
          color: isCopied ? "#15803d" : "#6B7280",
          flex: "none",
          bgcolor: isCopied ? "#dcfce7" : undefined,
          opacity: 0,
          transition: "opacity 120ms ease",
          ".MuiDataGrid-row:hover &, .MuiDataGrid-row:focus-within &, &:focus-visible":
            {
              opacity: 1,
            },
          "& .MuiChip-icon": {
            color: isCopied ? "#15803d" : "#6B7280",
          },
        }}
        variant="outlined"
      />
    </Tooltip>
  );
};
